/**
 * QR Screen — Anon Detection Module
 *
 * Extracted from QRScreen.js (Step 1 of qr-screen-refactor).
 * Handles the two detection layers (postgres_changes + polling) and the
 * full handleAnonDetected response: timing, profile wait, metadata build,
 * confirmation send, navigation, and background location backfill.
 */

import { generateMessageId } from '../../utils/messageUtils';
import { getProfileUserId } from '../../utils/identityUtils';
import { buildProfilePayload } from '../../utils/profilePayload';
import { startTiming, mark, flush } from '../../services/connectionTiming';
import { isSansChatVariant } from '../../utils/exchangeVariant';
import { ALL_CONTACT_KEYS } from '../../utils/contactKeys';
import {
  hasLocationPermission,
  getLocationWithVenue,
  formatLocationForDisplay,
  hasVenueOrAddress,
} from '../../services/location';
import { supabase } from '../../services/supabase';
import {
  checkAnonConnected,
  checkHostConfirmed,
  resetAnonConnected,
  STALE_THRESHOLD_MS,
} from '../../services/activeSession';
import {
  logConnectionError,
  logConnectionLocation,
  logFunnelConnectedAsSimple,
} from '../../services/analytics/eventLogger';
import { getUserTier, TIER_SIMPLE } from '../../utils/userTier';
import { markSimpleHostedExchange } from '../../utils/spMilestones';
import { sendMessage } from '../../services/chat/chatService';
import { persistMessage } from '../../services/chat/messageStore';
import {
  buildSystemMeetingMessage,
  buildMetadataUpdatedMessage,
} from '../../services/chat/messageBuilders';
import { currentUtcOffsetMinutes } from '../../utils/dateHelpers';
import { MEETING_KIND, mintMeetingKey } from '../../services/meetings/meetingRule';
import { stageConnectSheets } from '../../services/exchange/connectSheets';
import { handleReturningGuest, enterExistingRoom } from './returningGuest';
import {
  saveConnectionMetadata,
  upsertConnectionView,
  saveContactSharingStatus,
  getProfileData,
} from '../../services/storage/userDataStorage';
import { formatSystemMessageLocation } from '../../utils/locationFormatters';

// Module-level atomic guard — shared between detection layers and handler.
// Prevents duplicate handleAnonDetected execution across detection paths.
let anonDetectedRef = false;

/**
 * Reset the detection guard. Called when QRScreen opens a new session.
 */
export const resetDetectionGuard = () => {
  anonDetectedRef = false;
};

/**
 * Handle Anon detection: build metadata, send confirmation, navigate, backfill location.
 *
 * @param {string} source - Detection source label ('DB-realtime' | 'Poll')
 * @param {string} anonConnectedAt - ISO timestamp of when anon connected
 * @param {Object} config - Explicit dependencies (previously closures)
 * @param {string} config.chatRoomId
 * @param {string} config.profileUserId
 * @param {boolean} config.isRejoinedSession - activateSession returned a different room than requested
 * @param {React.RefObject} config.profileRef
 * @param {React.RefObject} config.locationDataRef
 * @param {React.RefObject} config.locationPromiseRef
 * @param {Function} config.updateConnectionMetadata
 * @param {Function} config.updateSessionChatRoomId
 * @param {Function} config.navigateTo
 * @param {Function} config.onClose - Called after navigation
 * @param {Function} config.confirmHostConnection
 * @param {Function} [config.onFormed] - (chatRoomId) — a Simple's NEW code page
 *   (signup E3a, under isNewExperience()): formation runs unchanged, but the
 *   host does not navigate or close — this is called instead, and the code page
 *   raises its Share sheet and lands on new People itself. Full hosts never pass
 *   it (E3b).
 */
export const handleAnonDetected = async (source, anonConnectedAt, config, returningRoomId) => {
  const {
    chatRoomId,
    profileUserId,
    isRejoinedSession,
    profileRef,
    locationDataRef,
    locationPromiseRef,
    updateConnectionMetadata,
    updateSessionChatRoomId,
    navigateTo,
    onClose,
    confirmHostConnection,
    onFormed,
  } = config;

  // Atomic guard — module-level variable is synchronous so no race between detection paths
  if (anonDetectedRef) {
    console.log(`[QR_SCREEN] ${source}: Already handled (ref guard), skipping`);
    return;
  }
  anonDetectedRef = true;

  // A known guest re-scanning: they marked this live session with the room the two
  // already share — no new room forms; the host moves into that conversation
  if (returningRoomId) {
    const handled = await handleReturningGuest(returningRoomId, config);
    if (!handled) anonDetectedRef = false; // refused room: keep listening
    return;
  }

  // Rejoin-scoped formation guard: a rejoined session can be a ghost mark
  // targeting an already-formed room (stale anon_connected=true). Re-running
  // formation would re-date meetingDate and upsert over the origin system
  // message. host_confirmed_at is set at the END of formation, so a legit
  // rejoin mid-exchange (≤10s protect window) still has it NULL and passes.
  // Only a definitive `true` skips — a failed query (null) must not block a
  // real exchange. Fresh exchanges never rejoin, so the hot path pays nothing.
  if (isRejoinedSession) {
    const formed = await checkHostConfirmed(chatRoomId);
    if (formed === true) {
      // Clear the mark either way so it can't capture the next QR open
      resetAnonConnected(profileUserId);

      // LIVE RE-SCAN vs STALE GHOST (device pass 1k: re-scans left the host
      // stranded on the QR screen): a mark seconds old means a connected
      // person is standing there re-scanning — greet them by navigating to
      // the EXISTING room. Formation stays skipped either way (re-forming
      // would re-date the meeting and overwrite the origin system message),
      // so People gets NO justFormed params and no venue re-confirm.
      const markAgeMs = anonConnectedAt ? Date.now() - new Date(anonConnectedAt).getTime() : null;
      if (markAgeMs !== null && markAgeMs < STALE_THRESHOLD_MS) {
        console.log(
          `[QR_SCREEN] ${source}: Rejoined room already formed — live re-scan, navigating without formation`
        );
        enterExistingRoom(chatRoomId, config);
        return;
      }

      console.log(
        `[QR_SCREEN] ${source}: Rejoined room already formed — ghost mark, skipping formation`
      );
      // Layer 1 (markAnonConnected's is_active filter) should make this
      // unreachable — if it fires, a mark landed on an ACTIVE session pointing
      // at a formed room. Log like Layer 1's ZERO_ROWS so the backstop is
      // observable, not silent.
      logConnectionError('database', {
        operation: 'formation_guard',
        code: 'GHOST_FORMATION_BLOCKED',
        message: 'Rejoined room already formed — formation skipped, mark cleared',
        chatRoomId,
      });
      // Re-arm detection — a swallowed ghost must not eat the NEXT real scan
      // this QR open (the ref guard would otherwise skip it)
      anonDetectedRef = false;
      return;
    }
  }

  console.log(
    `[QR_SCREEN] ${source}: Anon detected! Building metadata and sending confirmation...`
  );
  // Connection timing: T0 starts here — when the anon actually scans
  startTiming(chatRoomId, 'profile', profileRef.current?.name);
  const detectionMeta = { method: source };
  if (anonConnectedAt) {
    const latencyMs = Date.now() - new Date(anonConnectedAt).getTime();
    detectionMeta.detection_latency_ms = latencyMs;
    console.log(`[QR_SCREEN] Detection latency: ${latencyMs}ms (scan→detect)`);
  }
  mark('anon_detected', detectionMeta);

  // Wait for profile data if not yet available
  if (!profileRef.current) {
    console.log(`[QR_SCREEN] ${source}: Profile not ready, waiting...`);
    for (let i = 0; i < 20 && !profileRef.current; i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
    if (!profileRef.current) {
      console.error(`[QR_SCREEN] ${source}: Profile still not ready`);
      return;
    }
  }

  // profileRef is snapshotted at QR init, and sans-chat's survive-close keeps
  // that init alive across open/close cycles — channels added on the profile
  // screen after the first open (SP: Add-your-socials) never reach the ref.
  // The card sent must be the profile as SAVED at scan time — re-read it here.
  const freshProfile = await getProfileData();
  if (freshProfile) {
    profileRef.current = freshProfile;
  }

  // === INSTANT PATH: metadata → send → navigate ===
  // Include location if already resolved (common case — GPS ran on QR screen).
  // If not available, background IIFE backfills and sends metadata_updated.
  const currentTimestamp = new Date().toISOString();

  const metadata = {
    meetingDate: currentTimestamp,
    meetingUtcOffset: currentUtcOffsetMinutes(),
    meetingKey: mintMeetingKey(),
    via: 'scan',
    lastUpdated: currentTimestamp,
    exchangeType: 'anon_profile',
    // Venue displays only after the Profile's human confirmation (contract #7);
    // formatSystemMessageLocation gates on this flag. Parity with qrPXPExchange.
    venueConfirmed: false,
  };

  // Include location if already resolved — avoids two-stage metadata delivery
  // which can cause missing location (dropped real-time notification) or
  // duplicate system messages (update races with state transitions).
  const existingLocationData = locationDataRef.current;
  if (existingLocationData) {
    metadata.locationData = existingLocationData;
    if (hasVenueOrAddress(existingLocationData)) {
      metadata.location = formatLocationForDisplay(existingLocationData);
    }
  }

  // Save metadata BEFORE navigation so ProfileConnectionPage has it on mount
  await saveConnectionMetadata(chatRoomId, metadata);
  await updateConnectionMetadata(chatRoomId, metadata);
  console.log(`[QR_SCREEN] ${source}: Minimal metadata saved`);

  // Create the origin banner BEFORE navigation so the Anon sees it on first load.
  // Time-first: persist with the meetingDate even if the venue hasn't resolved yet
  // (content is '' → the banner shows the time only; the venue is added when it
  // arrives). Uses formatSystemMessageLocation + meetingDate, NOT the old baking
  // formatSystemMessage (which stranded a non-aging, location-less banner).
  const systemContent = formatSystemMessageLocation(metadata);
  await persistMessage(
    chatRoomId,
    buildSystemMeetingMessage({
      id: `system_${chatRoomId}`,
      content: systemContent,
      meetingDate: metadata.meetingDate,
      meetingUtcOffset: metadata.meetingUtcOffset,
    })
  );

  // === SEND CONFIRMATION (profile_data_response) — IMMEDIATE ===
  try {
    const currentProfile = profileRef.current;
    // SECURITY: Explicit payload — never spread currentProfile.
    // Sans-chat — "to scan is to get your card" (user-settled 2026-08-07,
    // supersedes the first-name+photo auto-share FOR this flow): the auto
    // response IS the full card — name as entered + every FILLED channel.
    // The profile-edit screen ("This is what your connections see") is the
    // person's control surface. Legacy flow keeps the first-name+photo
    // allowlist (full contacts only via SharingSelectionOverlay).
    const profileDataToSend = isSansChatVariant()
      ? buildProfilePayload(
          currentProfile,
          [
            'name',
            'photoUrl',
            ...ALL_CONTACT_KEYS.filter((k) => currentProfile[k]),
            // City rides the card when filled — the profile screen ("This is
            // what your connections see") is the consent surface, same as
            // channels. Receiving surfaces gate on the sharedFields flag
            // (isHometownShared: LIVES IN on detail, city on People cards).
            ...(currentProfile.currentCity ? ['currentCity'] : []),
          ],
          {
            profileUserId: profileUserId,
            // Tier marker for the peer's Royale ring (Full-only, user ruling
            // 2026-08-08). MISSING reads as Full on the receiving side —
            // legacy hosts were all Full, so old stored cards keep their ring.
            senderAccountStatus: getUserTier() === TIER_SIMPLE ? 'anon' : 'full',
          }
        )
      : buildProfilePayload(currentProfile, ['name', 'photoUrl'], {
          name: currentProfile.name.split(' ')[0],
          nameSharing: 'first',
          profileUserId: profileUserId,
          // The automatic first-name + photo reply, not a share: the new
          // guest card stays at 0a until the host really shares (CARD §5b;
          // Mark 2026-10-20). Additive — the old design never reads it, and
          // a real share replaces the whole record, so the tag goes with it.
          autoReply: true,
        });

    const result = await sendMessage(chatRoomId, {
      id: generateMessageId(),
      type: 'profile_data_response',
      sender: 'profile',
      senderId: getProfileUserId(),
      senderProfileUserId: profileUserId,
      profileData: profileDataToSend,
      connectionMetadata: metadata,
      timestamp: currentTimestamp,
    });

    if (result.error) {
      console.error(`[QR_SCREEN] ${source}: Send failed, retrying once:`, result.error);
      await new Promise((r) => setTimeout(r, 1000));
      await sendMessage(chatRoomId, {
        id: generateMessageId(),
        type: 'profile_data_response',
        sender: 'profile',
        senderId: getProfileUserId(),
        senderProfileUserId: profileUserId,
        profileData: profileDataToSend,
        connectionMetadata: metadata,
        timestamp: currentTimestamp,
      });
    } else {
      console.log(`[QR_SCREEN] ${source}: Confirmation sent successfully`);
      mark('confirmation_sent');
      // flush() moved to ProfileConnectionPage mount — that's the real "in chat" moment
      // (legacy flow; sans-chat flushes after the People navigation below)
    }

    // Sans-chat: the full card went out — record it truthfully on the host's
    // own sharing ledger (gates venue/camera affordances downstream).
    if (isSansChatVariant()) {
      await saveContactSharingStatus(chatRoomId, true);
    }
  } catch (error) {
    console.error(`[QR_SCREEN] ${source}: Error sending confirmation:`, error);
  }

  // A first meeting: both sheets on the conversation page ("Where you met", share)
  stageConnectSheets(chatRoomId, MEETING_KIND.FIRST);

  // Write to permanent connections table (Host side, Anon↔Profile)
  // Chain connection_views + meeting creation after connections row exists
  confirmHostConnection(chatRoomId, profileUserId, 'anon_profile').then(() => {
    upsertConnectionView(chatRoomId, profileUserId, { metadata });
  });

  // Reset anon_connected so the activateSession guard doesn't fire on next QR open.
  // Without this, anon_connected=true persists and blocks all future sessions.
  resetAnonConnected(profileUserId);

  // Navigate — IMMEDIATE. Sans-chat: the host lands on PEOPLE, where the
  // just-formed card takes over (chat never mounts — D1); fresh params force
  // the list reload and met-desc sort pins the new card on top. Timing
  // flush() rides here (its old home was the chat-page mount).
  updateSessionChatRoomId(chatRoomId, true);
  if (onFormed) {
    // The new code page owns what comes next: its Share sheet rises over the
    // code, the waiting gate holds for the guest's name, and either way the
    // host lands on new People (E3a Step 9; every host since E3b-1 step 9).
    // Funnel step 3 (hero metric) stays Simple-only: an SP hosted a new
    // exchange. A Full host needs no event — KPI 1 counts `connections` rows.
    if (getUserTier() === TIER_SIMPLE) {
      logFunnelConnectedAsSimple(chatRoomId);
      markSimpleHostedExchange();
    }
    onFormed(chatRoomId, { nameKnown: false, repeat: false });
    flush();
  } else if (isSansChatVariant()) {
    // Stale cleanup only — the pre-scan waiting flag is no longer written
    // (device pass 2 ruling), but a session from an older build may carry it
    sessionStorage.removeItem('qr_waiting_room');
    // The just-formed card opens at its venue stage
    sessionStorage.setItem(`host_card_stage_${chatRoomId}`, 'venue');
    // Funnel step 3 (hero metric, repeatable): an SP hosted a new exchange —
    // every step-3 manufactures a step-1 for the person they connected
    if (getUserTier() === TIER_SIMPLE) {
      logFunnelConnectedAsSimple(chatRoomId);
      // App state, not analytics: the People page's QR reminder retires when
      // the thing it asks for HAPPENS rather than when it has been seen. This
      // is that moment. Stamped here, beside the funnel event, because this is
      // the one place that knows an SP just hosted an exchange.
      markSimpleHostedExchange();
    }
    navigateTo('profile-connections-list', { justFormed: chatRoomId, formedAt: Date.now() });
    flush();
  } else {
    navigateTo('profile-connection-page');
  }
  if (onClose && !onFormed) {
    onClose();
  }

  // === BACKGROUND IIFE: resolve location if not already included, backfill, push to Anon ===
  (async () => {
    try {
      // If location was already included in the initial response, just log analytics
      if (existingLocationData) {
        logConnectionLocation(chatRoomId, existingLocationData, 'radar');
        return;
      }

      // Location wasn't available at detection time — resolve now
      let locationData = null;
      if (locationPromiseRef.current) {
        try {
          locationData = await locationPromiseRef.current;
        } catch (err) {
          console.log('[QR_SCREEN] Background IIFE: location promise failed:', err);
        }
      }
      if (!locationData) {
        const hasPerm = await hasLocationPermission();
        if (hasPerm) {
          try {
            locationData = await getLocationWithVenue();
            locationDataRef.current = locationData;
          } catch (err) {
            console.log('[QR_SCREEN] Background IIFE: fresh fetch failed:', err);
          }
        }
      }

      if (!locationData) {
        console.log('[QR_SCREEN] Background IIFE: no location available, skipping backfill');
        return;
      }

      // Build updated metadata with location
      const updated = {
        ...metadata,
        locationData,
        lastUpdated: new Date().toISOString(),
      };
      if (hasVenueOrAddress(locationData)) {
        updated.location = formatLocationForDisplay(locationData);
      }

      // Backfill localStorage + context
      await saveConnectionMetadata(chatRoomId, updated);
      await updateConnectionMetadata(chatRoomId, updated);
      console.log(
        `[QR_SCREEN] Background IIFE: metadata backfilled (display: ${updated.location ? 'yes' : 'coords only'})`
      );

      // Re-persist the shared banner so the persisted copy picks up the
      // late-resolving location — the pre-navigation persist ran before the
      // venue/neighborhood resolved (content ''). Formatter gate keeps the
      // content neighborhood-level until the venue is confirmed.
      await persistMessage(
        chatRoomId,
        buildSystemMeetingMessage({
          id: `system_${chatRoomId}`,
          content: formatSystemMessageLocation(updated),
          meetingDate: updated.meetingDate,
          meetingUtcOffset: updated.meetingUtcOffset,
        })
      );

      // Push location to Anon via metadata_updated
      await sendMessage(
        chatRoomId,
        buildMetadataUpdatedMessage({
          connectionMetadata: updated,
          senderProfileUserId: profileUserId,
        })
      );
      console.log('[QR_SCREEN] Background IIFE: metadata_updated sent to Anon');

      // Analytics: log connection location for Activity feed
      // Own try/catch so analytics can't propagate to outer catch
      try {
        logConnectionLocation(chatRoomId, locationData, 'radar');
      } catch (e) {
        /* analytics non-critical */
      }
    } catch (err) {
      console.error('[QR_SCREEN] Background IIFE error:', err);
    }
  })();
};

/**
 * Set up the two detection layers for Anon connection.
 *
 * Layer 1: postgres_changes subscription — fast detection (~500ms)
 * Layer 2: Polling fallback — guaranteed detection (~1.5s worst case)
 *
 * @param {string} chatRoomId
 * @param {Function} onAnonDetected - Called with (source, anonConnectedAt, returningRoomId)
 * @returns {Function} Cleanup function (unsubscribe + clearInterval)
 */
export const setupDetectionLayers = (chatRoomId, onAnonDetected) => {
  // Layer 1: postgres_changes subscription — fast detection (~500ms)
  // Not guaranteed (Realtime can drop change events) but catches most cases
  const dbChannel = supabase
    .channel(`session-watch-${chatRoomId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'active_sessions',
        filter: `chat_room_id=eq.${chatRoomId}`,
      },
      (payload) => {
        if (payload.new.anon_connected && !payload.old.anon_connected) {
          console.log('[QR_SCREEN] DB realtime: Anon connected!');
          onAnonDetected(
            'DB-realtime',
            payload.new.anon_connected_at,
            payload.new.returning_room_id || null
          );
        }
      }
    )
    .subscribe();

  // Layer 2: Polling fallback — guaranteed detection (~1.5s worst case)
  // Runs every 1.5s, stops as soon as any path detects Anon
  const pollInterval = setInterval(async () => {
    const result = await checkAnonConnected(chatRoomId);
    if (result) {
      console.log('[QR_SCREEN] Poll: Anon connected!');
      clearInterval(pollInterval);
      onAnonDetected('Poll', result.anon_connected_at, result.returning_room_id);
    }
  }, 1500);

  // Return cleanup function
  return () => {
    dbChannel.unsubscribe();
    clearInterval(pollInterval);
  };
};
